import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import { TranslateService } from '@codeandweb/ngx-translate';
import { LanguageType, MenuType } from '../enum/app.enum';
import { LocalStorageService } from './localStorage.service';
import { HistoryList, NewsItem } from '../interfaces/app.interface';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  public menuDrawer: boolean = false;
  public menuItem = [
    { label: 'ABOUT', id: MenuType.About },
    { label: 'MEMBER', id: MenuType.Member },
    { label: 'HISTORY', id: MenuType.History },
    { label: 'NEWS', id: MenuType.News }
  ];
  public currentMenu: MenuType = MenuType.About;
  public languageArr = [
    { label: '한국어', id: LanguageType.Ko },
    { label: 'English', id: LanguageType.En },
    { label: 'Français', id: LanguageType.Fr },
    { label: '日本語', id: LanguageType.Jp }
  ];

  public usedLanguage = signal<LanguageType>(LanguageType.Ko);
  public mainHomeScrollElement!: HTMLElement;
  public homeAboutTop: number = 0;
  public homeMembersTop: number = 0;
  public homeHistoryTop: number = 0;
  public homeNewsTop: number = 0;
  public isLanguageMenuOpen: boolean = false;
  private translateService = inject(TranslateService);
  private localStorageService = inject(LocalStorageService);

  constructor() {}

  goExternal(fullUrl: string) {
    window.open(fullUrl, '_blank');
  }

  openLanguageMenu() {
    this.isLanguageMenuOpen = true;
  }

  closeLanguageMenu() {
    this.isLanguageMenuOpen = false;
  }

  // 딜레이 시키는 함수
  async delay(ms: number) {
    await new Promise(resolve => setTimeout(() => resolve(null), ms)).then(() => console.log('fired'));
  }

  async getHistoryJsonData(): Promise<HistoryList[]> {
    const timestamp = new Date().getTime();
    const url = `${environment.awsConfig.historyS3Url}?t=${timestamp}`;

    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: { Accept: 'application/json' }
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  getDefaultLanguage(): LanguageType {
    try {
      let defaultLanguage;
      if (/^ko\b/.test(navigator.language)) {
        defaultLanguage = LanguageType.Ko;
      } else if (this.isFrench()) {
        defaultLanguage = LanguageType.Fr;
      } else if (this.isJapan()) {
        defaultLanguage = LanguageType.Jp;
      } else {
        defaultLanguage = LanguageType.En;
      }

      return defaultLanguage;
    } catch (e) {
      throw new Error(`getDefaultLanguage : ${e}`);
    }
  }

  isFrench(): boolean {
    try {
      return /^fr|fr-BE|fr-CA|fr-CH|fr-LU\b/.test(navigator.language);
    } catch (e) {
      throw new Error(`isFrench : ${e}`);
    }
  }

  isJapan(): boolean {
    try {
      return /^ja|jp|ja-JP\b/.test(navigator.language);
    } catch (e) {
      throw new Error(`isJapan : ${e}`);
    }
  }

  /**
   * 뉴스 데이터 가져오기
   * @return {Promise<any>}
   */
  async getNewsList(): Promise<NewsItem[] | null> {
    const res = await fetch(`${environment.server_url}/news/${this.usedLanguage()}`);
    if (res.status === 200) {
      return await res.json();
    } else {
      return null;
    }
  }

  async getSnsLink() {
    const time = new Date().getTime();
    const res = await fetch(`${environment.awsConfig.snsS3url + time}/sns`);

    if (res.status === 200) {
      const data = await res.json();
      return await data.footerLink;
    } else {
      return null;
    }
  }

  changeLanguage(language: LanguageType) {
    this.usedLanguage.set(language);
    this.translateService.use(language);
    this.localStorageService.setLanguage(language);
    document.documentElement.setAttribute('language-type', language);
    document.getElementsByTagName('html')[0].lang = language;
    if (this.isLanguageMenuOpen) {
      this.closeLanguageMenu();
    }
  }

  goToSection(id: MenuType) {
    this.currentMenu = id;

    let scrollTo: number = 0;
    switch (id) {
      case MenuType.About:
        scrollTo = this.homeAboutTop;
        break;
      case MenuType.Member:
        scrollTo = this.homeMembersTop;
        break;
      case MenuType.History:
        scrollTo = this.homeHistoryTop;
        break;
      case MenuType.News:
        scrollTo = this.homeNewsTop;
        break;
    }
    this.mainHomeScrollElement.scrollTo({
      top: scrollTo,
      behavior: 'smooth'
    });
  }
}
